import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link, useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  Clock, 
  Award, 
  TrendingUp,
  Play,
  CheckCircle,
  ArrowRight,
  Calendar,
  Target,
  Star,
  Users,
  BarChart3
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { coursesAPI, authAPI, registrationAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const Dashboard = () => {
  const { user } = useAuth();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate()
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  useEffect(() => {
    // navigate("/profile")
    const fetchRegistrations = async () => {
      try {
        const response = await registrationAPI.getMyRegistrations();
        setRegistrations(response.data.data || []);
      } catch (error) {
        console.error('Error fetching course registrations:', error);
        setRegistrations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRegistrations();
  }, []);

  const stats = [
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
      {/* Header */}
      <section className="bg-gradient-to-br from-primary-600 to-accent-600 text-white py-12">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Welcome back, {user?.firstName}!
            </h1>
            <p className="text-xl text-blue-100">
              You have {registrations.length} registered courses

              
            </p>
          </motion.div>
        </div>
      </section>

      <div className="container-custom py-12">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Stats Grid */}
          <motion.div
            variants={itemVariants}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
          >
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  whileHover={{ y: -5 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 dark:text-gray-300 text-sm font-medium">
                        {stat.title}
                      </p>
                      <p className={`text-3xl font-bold ${stat.color} mt-2`}>
                        {stat.value}
                      </p>
                    </div>
                    <div className={`w-12 h-12 ${stat.bgColor} ${stat.darkBgColor} rounded-xl flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* My Courses */}
            <motion.div variants={itemVariants} className="lg:col-span-2">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    My Course Registrations
                  </h2>
                  <Link
                    to="/courses"
                    className="text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium"
                  >
                    Browse All
                  </Link>
                </div>

                {registrations.length > 0 ? (
                  <div className="space-y-4">
                    {registrations.map((registration) => (
                      <motion.div
                        key={registration.id}
                        whileHover={{ x: 5 }}
                        className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200"
                      >
                        <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-accent-100 dark:from-primary-900 dark:to-accent-900 rounded-xl flex items-center justify-center">
                          {registration.course.image ? <img src={registration.course.image} className="w-8 h-8 text-primary-600 dark:text-primary-400" /> : <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                            <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          </div>}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                            {registration.course.title}
                          </h3>
                          {/* <p className="text-sm text-gray-600 dark:text-gray-300">
                            {registration.course.instructor ? 
                              `by ${registration.course.instructor.firstName} ${registration.course.instructor.lastName}` : 
                              'Instructor information unavailable'}
                          </p> */}
                          <div className="flex items-center space-x-4 mt-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {registration.course.category}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {registration.course.courseType}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              registration.status === 'completed'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : registration.status === 'confirmed'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                : registration.status === 'cancelled'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            }`}>
                              {registration.status.charAt(0).toUpperCase() + registration.status.slice(1)}
                            </span>
                          </div>
                        </div>

                        <div className="text-right">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {new Date(registration.registrationDate).toLocaleDateString()}
                            </div>
                            <div className="mt-1">
                              <div
                                className="text-xs text-gray-500 dark:text-gray-400"
                              >
                                {registration.status === 'pending' ? 'Awaiting confirmation' : 
                                 registration.status === 'confirmed' ? 'Registration confirmed' : 
                                 registration.status === 'cancelled' ? 'Registration cancelled' : 
                                 'Course completed'}
                              </div>
                            </div>
                          </div>

                          <Link
                            to={`/courses/${registration.course.slug || registration.course.id}`}
                            className="p-2 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-full transition-colors duration-200"
                          >
                            <ArrowRight className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                          </Link>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      No courses yet
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-6">
                      Start your learning journey by enrolling in a course
                    </p>
                    <Link to="/courses" className="btn btn-primary">
                      Browse Courses
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Sidebar */}
            <motion.div variants={itemVariants} className="space-y-6">
              {/* Quick Actions */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Quick Actions
                </h3>
                <div className="space-y-3">
                  <Link
                    to="/courses"
                    className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <BookOpen className="w-5 h-5 text-primary-600" />
                    <span className="text-gray-700 dark:text-gray-300">Browse Courses</span>
                  </Link>
                  <Link
                    to="/profile"
                    className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Users className="w-5 h-5 text-primary-600" />
                    <span className="text-gray-700 dark:text-gray-300">Edit Profile</span>
                  </Link>
                </div>
              </div>

              {/* Learning Goals */}
              {/* <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Learning Goals
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600 dark:text-gray-300">This Week</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">3/5 hours</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-full">
                      <div className="h-2 bg-primary-600 rounded-full" style={{ width: '60%' }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600 dark:text-gray-300">This Month</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">12/20 hours</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-full">
                      <div className="h-2 bg-accent-600 rounded-full" style={{ width: '60%' }} />
                    </div>
                  </div>
                </div>
              </div> */}

              {/* Recent Activity */}
              {/* <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Recent Activity
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      Completed React Fundamentals
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      Started Machine Learning course
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      Earned JavaScript certificate
                    </span>
                  </div>
                </div>
              </div> */}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;

